// React, Next
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import BulletGrid from '@/components/sections/BulletGrid'
import { translateHref } from 'assets/functions/links'

// Config
import nextI18nextConfig from 'next-i18next.config'

// Style
import themeColors from 'assets/theme/colors'
import { getFontBodySmall } from '@/assets/theme/fonts'

type Block = {
  eyebrow: string
  title: string
  /** Lead paragraphs, ending with the line that introduces the list. */
  intro: string[]
  items: string[]
  /** Closing paragraph shown under the list, where the content has one. */
  outro?: string
  suited: string
  href: string
}

export default function ServiceDetailsSection() {
  const { t } = useTranslation('services')

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const blocks: Block[] = [
    {
      eyebrow: t('details.01.title'),
      title: t('details.01.tagline'),
      intro: [t('details.01.intro1'), t('details.01.listTitle')],
      items: [
        t('details.01.items.01'),
        t('details.01.items.02'),
        t('details.01.items.03'),
        t('details.01.items.04'),
        t('details.01.items.05'),
        t('details.01.items.06'),
        t('details.01.items.07'),
        t('details.01.items.08')
      ],
      suited: t('details.01.suited'),
      href: translateHref(currentLocale, 'ot-cybersecurity')
    },
    {
      eyebrow: t('details.02.title'),
      title: t('details.02.tagline'),
      intro: [t('details.02.intro1'), t('details.02.listTitle')],
      items: [
        t('details.02.items.01'),
        t('details.02.items.02'),
        t('details.02.items.03')
      ],
      outro: t('details.02.outro'),
      suited: t('details.02.suited'),
      href: translateHref(currentLocale, 'ot-awareness-training')
    },
    {
      eyebrow: t('details.03.title'),
      title: t('details.03.tagline'),
      intro: [
        t('details.03.intro1'),
        t('details.03.intro2'),
        t('details.03.listTitle')
      ],
      items: [
        t('details.03.items.01'),
        t('details.03.items.02'),
        t('details.03.items.03'),
        t('details.03.items.04'),
        t('details.03.items.05'),
        t('details.03.items.06'),
        t('details.03.items.07'),
        t('details.03.items.08'),
        t('details.03.items.09')
      ],
      suited: t('details.03.suited'),
      href: translateHref(currentLocale, 'test-equipment-modernization')
    }
  ]

  const getBlock = (block: Block) => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={block.eyebrow}
        title={block.title}
        intro={block.intro}
      />

      <BulletGrid items={block.items} columns={2} />

      {block.outro && (
        <Typography
          component="p"
          sx={{
            marginTop: '1.5rem',
            fontSize: getFontBodySmall(),
            lineHeight: 1.7,
            color: themeColors.gray.defaultText,
            maxWidth: '52rem'
          }}
        >
          {block.outro}
        </Typography>
      )}

      <Typography
        component="p"
        sx={{
          marginTop: '1.75rem',
          paddingLeft: '0.9rem',
          borderLeft: `3px solid ${themeColors.success.main}`,
          fontSize: getFontBodySmall(),
          lineHeight: 1.7,
          color: themeColors.gray.defaultText,
          maxWidth: '52rem'
        }}
      >
        <Box component="span" sx={{ fontWeight: 700 }}>
          {t('details.suitedLabel')}
        </Box>{' '}
        {block.suited}
      </Typography>

      <Link href={block.href} style={{ textDecoration: 'none' }}>
        <Button
          variant="outlined"
          endIcon={<ArrowForwardIosIcon sx={{ transform: 'scale(0.7)' }} />}
          sx={{
            marginTop: '1.75rem',
            borderColor: themeColors.success.main,
            color: themeColors.success.darker,
            borderRadius: '4px',
            padding: '0.6rem 1.4rem',
            fontWeight: 600,
            letterSpacing: '0.03em',
            '&:hover': {
              borderColor: themeColors.success.darker,
              background: `${themeColors.success.main}14`
            }
          }}
        >
          {t('learnMore')}
        </Button>
      </Link>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <>
      {blocks.map((block, idx) => (
        <Section
          key={block.eyebrow}
          // Alternating bands keep the three services visually separate.
          background={
            idx % 2 === 0
              ? themeColors.section.light
              : themeColors.kroemtech.background
          }
          paddingTop="5rem"
          paddingBottom="5rem"
          content={getBlock(block)}
        />
      ))}
    </>
  )
}
